import { useAccount } from 'wagmi';
import {
  useCurrentPosition,
  useEffectiveLeverage,
  useMarginUsage,
  useNewPositionSize,
  useProjectedEffectiveLeverage,
  useProjectedMaintenanceMargin,
  useProjectedMarginUsage,
  useTotalPositionValue,
} from '../components/TradePage/TradeForm/hooks/calculationHooks';
import {
  getPriceToUseFromState,
  useTradeFormStore,
} from '../components/TradePage/TradeForm/TradeFormState';
import { multiply } from '../lib/math';
import { useAccountEquity } from './useAccountEquity';
import { useAccountMaintenanceMargin } from './useAccountMaintenanceMargin';
import { useAccountMargin } from './useAccountMargin';
import { useAccountUPnl } from './useAccountUPnl';
import { usePositions } from './usePositions';
import { useProductPricesSub } from './useProductPricesSub';
import { ProductType } from './useProducts';
import { useActiveSelectedSubaccount } from './useSelectedSubaccount';

/**
 * Hook to compose all the calculations needed for TradeFormAccountSummary
 */
export function useTradeFormAccountSummaryData(product: ProductType) {
  // Get wallet and account data
  const { address: walletAddress } = useAccount();
  const selectedSubaccount = useActiveSelectedSubaccount();
  const { data: maintenanceMargin } = useAccountMaintenanceMargin();
  const { data: accountUPnl } = useAccountUPnl();
  const { data: positions } = usePositions({ subaccountId: selectedSubaccount?.id });
  const { data: marginData } = useAccountMargin();
  const accountEquity = useAccountEquity();

  // Get trade form state
  const side = useTradeFormStore(({ side }) => side);
  const amount = useTradeFormStore(({ amount }) => amount);
  const postOnly = useTradeFormStore(({ postOnly }) => postOnly);
  const priceToUse = useTradeFormStore((s) =>
    getPriceToUseFromState({
      orderType: s.orderType,
      limitPrice: s.limitPrice,
      executionPriceLong: s.executionPriceLong,
      executionPriceShort: s.executionPriceShort,
      side: s.side,
      midPrice: s.midPrice,
    })
  );

  // Get the current position
  const { size: currentPositionSize, avgEntryPrice: currentPosAvgEntryPrice } = useCurrentPosition(
    positions,
    product.id
  );

  const { data: pricesByProductId } = useProductPricesSub();

  // Get account data
  const subaccountTotalBalance = marginData?.totalBalance;
  const availableMargin = marginData?.totalAvailable || 0n;
  const oraclePrice = pricesByProductId?.[product.id]?.oraclePrice;

  // Calculate new position size
  const newPosSize = useNewPositionSize(currentPositionSize, amount, side);

  // Calculate total position value
  const totalPositionValue = useTotalPositionValue(positions, pricesByProductId);

  const currentLeverage = useEffectiveLeverage({
    totalPositionValue,
    balance: subaccountTotalBalance,
  });

  const projectedLeverage = useProjectedEffectiveLeverage({
    totalPositionValue,
    subaccountTotalBalance,
    newPosSize,
    currentPositionSize,
    currentPosAvgEntryPrice,
    executionPrice: priceToUse,
    feeRate: postOnly ? product.makerFee : product.takerFee,
    orderValue: multiply(amount || 0n, priceToUse || 0n),
    side,
  });

  // Calculate projected maintenance margin
  const projectedMaintenanceMargin = useProjectedMaintenanceMargin({
    maintenanceMargin,
    currentPositionSize,
    newPositionSize: newPosSize,
    oraclePrice,
    maintenanceMarginRatio: product.maintenanceMarginRatio,
  });

  // Calculate margin usage
  const marginUsage = useMarginUsage({ maintenanceMargin, subaccountTotalBalance, accountUPnl });
  const projectedMarginUsage = useProjectedMarginUsage({
    maintenanceMargin,
    subaccountTotalBalance,
    currentPositionSize,
    newPositionSize: newPosSize,
    oraclePrice,
    maintenanceMarginRatio: product.maintenanceMarginRatio,
    avgEntryPrice: currentPosAvgEntryPrice,
    executionPrice: priceToUse,
    feeRate: postOnly ? product.makerFee : product.takerFee,
  });

  return {
    isConnected: !!walletAddress,
    currentAccountLeverage: currentLeverage,
    projectedAccountLeverage: amount ? projectedLeverage : undefined,
    marginUsage,
    projectedMarginUsage: amount ? projectedMarginUsage : undefined,
    maintenanceMargin,
    projectedMaintenanceMargin: amount ? projectedMaintenanceMargin : undefined,
    availableMargin,
    selectedSubaccount,
    accountEquity,
    accountUPnl,
  };
}
